import React, { useState, useEffect } from 'react';
import { FaMusic, FaHeadphones, FaBroadcastTower, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

// --- SÉLECTION DE RADIOS LIVE RÉPARÉES (VÉRIFIÉES SANS ERREUR) ---
export const MOODS = {
    roadtrip: { label: 'Roadtrip', videoId: '4xDzrJKXOOY', icon: '🚗' }, // Lofi Synthwave
    cozy: { label: 'Cozy Lofi', videoId: 'jfKfPfyJRdk', icon: '☕' },    // Lofi Girl
    hiphop: { label: 'Hip Hop', videoId: '7NOSDKb0H8M', icon: '🎤' },   // Lofi Hip Hop
    latino: { label: 'Latino', videoId: '8pI9N9n9k-M', icon: '💃' },    // Latino Mix
    party: { label: 'Party Mix', videoId: '5qap5aO4i9A', icon: '🎉' },  // Club Party
    jazz: { label: 'Jazz Live', videoId: 'Dx5qFacha3o', icon: '🎷' },   // Coffee Shop Jazz
    zen: { label: 'Zen Relax', videoId: 'lTRiuFIWV54', icon: '🧘' }     // Relaxing Music
};

export default function DJWidget() {
    const [currentMood, setCurrentMood] = useState<keyof typeof MOODS>('roadtrip');
    const [isPlaying, setIsPlaying] = useState(false);
    const [isChanging, setIsChanging] = useState(false);
    const [volume, setVolume] = useState(50);

    // Synchronisation avec le moteur global
    useEffect(() => {
        const handleRadioUpdate = (e: any) => {
            if (e.detail.mood) setCurrentMood(e.detail.mood);
            if (e.detail.isPlaying !== undefined) setIsPlaying(e.detail.isPlaying);
            if (e.detail.volume !== undefined) setVolume(e.detail.volume);
        };
        window.addEventListener('radioUpdate', handleRadioUpdate);
        return () => window.removeEventListener('radioUpdate', handleRadioUpdate);
    }, []);

    const sendCommand = (detail: any) => {
        window.dispatchEvent(new CustomEvent('changeRadio', { detail }));
    };

    const handleMoodChange = (mood: keyof typeof MOODS) => {
        if (currentMood === mood && isPlaying) return;
        setIsChanging(true);
        sendCommand({ mood, isPlaying: true });
        setTimeout(() => setIsChanging(false), 800);
    };

    const togglePlay = () => {
        sendCommand({ mood: currentMood, isPlaying: !isPlaying });
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVol = parseInt(e.target.value);
        setVolume(newVol);
        sendCommand({ volume: newVol });
    };

    return (
        <div className="bg-neutral-50 rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.4)] overflow-hidden max-w-[340px] sm:max-w-md mx-auto border-[6px] border-white flex flex-col h-full transform transition-all duration-500 hover:shadow-red-500/10 select-none">
            
            {/* --- HEADER DESIGN (DÉGRADÉ ROUGE PROFOND) --- */}
            <div className="bg-gradient-to-br from-red-700 via-red-600 to-red-800 p-6 flex items-center gap-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                <div className="relative z-10">
                    <div className="w-16 h-16 rounded-2xl border-2 border-white/50 rotate-3 overflow-hidden bg-black shadow-2xl transition-transform hover:rotate-0">
                        <video src="/GifForfy.MP4" autoPlay loop muted playsInline className="w-full h-full object-cover scale-110" />
                    </div>
                    {isPlaying && <div className="absolute -top-2 -left-2 bg-green-500 w-4 h-4 rounded-full border-2 border-white animate-ping"></div>}
                </div>
                <div className="text-white z-10">
                    <h3 className="text-2xl font-black italic leading-none tracking-tighter uppercase drop-shadow-md">DJ FORFY</h3>
                    <div className="flex items-center gap-1.5 mt-1 bg-black/20 w-fit px-2 py-0.5 rounded-full border border-white/10">
                        <FaVolumeUp className="text-[10px] animate-pulse" />
                        <p className="text-[9px] font-bold tracking-[0.2em] uppercase">DIRECT LIVE</p>
                    </div>
                </div>
            </div>

            {/* --- LISTE DES STATIONS (3D GRID) --- */}
            <div className="p-4 bg-slate-50 flex-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">VOTRE CO-PILOTE MUSICAL</p>
                <div className="grid grid-cols-2 gap-2.5 max-h-[180px] overflow-y-auto pr-1 scrollbar-hide">
                    {(Object.keys(MOODS) as Array<keyof typeof MOODS>).map((mood) => (
                        <button
                            key={mood}
                            onClick={() => handleMoodChange(mood)}
                            className={`relative py-3 px-3 rounded-2xl text-[11px] font-bold uppercase transition-all duration-200 border-b-[4px] active:border-b-0 active:translate-y-1 flex flex-col items-center gap-1
                                ${currentMood === mood && isPlaying 
                                    ? 'bg-red-600 text-white border-red-800 shadow-lg scale-[1.02]' 
                                    : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'}`}
                        >
                            <span className={`text-xl ${isChanging && currentMood === mood ? 'animate-spin' : ''}`}>{MOODS[mood].icon}</span>
                            <span className="truncate w-full text-center">{MOODS[mood].label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* --- CONTRÔLES BAS (VOLUME & PLAY) --- */}
            <div className="p-6 bg-slate-100 border-t border-slate-200 rounded-b-[2rem]">
                {/* VOLUME TACTILE */}
                <div className="flex items-center gap-3 mb-5 bg-white p-2.5 rounded-2xl shadow-inner border border-slate-200">
                    {volume === 0 ? <FaVolumeMute className="text-slate-400" /> : <FaVolumeUp className="text-red-600" />}
                    <input type="range" min="0" max="100" value={volume} onChange={handleVolumeChange} className="flex-1 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-red-600" />
                    <span className="text-[10px] font-bold text-slate-500 w-8 text-right">{volume}%</span>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isPlaying ? 'bg-red-100 text-red-600 shadow-sm' : 'bg-slate-200 text-slate-400'}`}>
                            <FaBroadcastTower className={isPlaying ? 'animate-pulse' : ''} />
                        </div>
                        <div className="flex flex-col text-left">
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter italic">Station Active</span>
                            <span className="text-sm font-black text-slate-800 uppercase leading-none">{MOODS[currentMood].label}</span>
                        </div>
                    </div>
                    <button onClick={togglePlay} className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-xl active:scale-95 border-b-4 ${isPlaying ? 'bg-red-600 text-white border-red-800' : 'bg-slate-800 text-white border-slate-950'}`}>
                        {isPlaying ? <div className="w-3 h-3 bg-white rounded-sm shadow-sm" /> : <div className="ml-1 border-y-[7px] border-y-transparent border-l-[11px] border-l-white" />}
                    </button>
                </div>

                {/* VISUALIZER 3D ÉCRAN */}
                <div className={`w-full h-14 rounded-2xl bg-neutral-900 border-2 border-white/20 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] relative overflow-hidden flex items-center justify-center transition-all ${isChanging ? 'brightness-150 blur-[1px]' : ''}`}>
                    {isPlaying ? (
                        <div className="flex items-center gap-1.5 h-6">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="w-1 rounded-full bg-red-500 animate-[bounce_1.5s_infinite] shadow-[0_0_8px_rgba(239,68,68,0.5)]" style={{ height: `${25 + Math.random() * 75}%`, animationDelay: `${i * 0.1}s` }}></div>
                            ))}
                        </div>
                    ) : <span className="text-[9px] text-white/10 font-black uppercase tracking-[0.3em]">STANDBY MODE</span>}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"></div>
                </div>
            </div>
        </div>
    );
}
